import React from 'react'
import { Settings2 } from 'lucide-react'
import { RuleNode, LoopNode, IfNode, TextRuleNode, ConditionEntry, ConditionGroup } from '../../types/codeIndex'
import { normalizeCodeItemReference } from '../../utils/codeReference'
import { isExclusionOperator, type RuleValidationConditionHighlight } from '../../utils/ruleValidation'
import InfoBadge from '../ui/InfoBadge'

export interface AvailabilityBadgeProps {
  value: RuleNode | null | undefined
  onConfigClick?: () => void
  showConfigIcon?: boolean
  disabled?: boolean
  conditionHighlights?: RuleValidationConditionHighlight[]
  conditionHighlightVariant?: 'error' | 'warning'
}

// Helper to format While rule display
function formatWhileDisplay(rule: LoopNode | undefined, expression: string): React.ReactNode {
  if (rule?.reference) {
    const conditionText = rule.condition === '!=' ? 'not equals to' 
      : rule.condition === '>' ? 'higher than'
      : rule.condition === '>=' ? 'higher or equal to'
      : rule.condition === '<' ? 'lower than'
      : rule.condition === '<=' ? 'lower or equal to'
      : rule.condition === '==' ? 'equal to'
      : rule.condition
    return (
      <>
        <InfoBadge variant="neutral">{rule.reference}</InfoBadge>
        <span className="text-sm text-gray-700">{conditionText}</span>
        <span className="text-sm text-gray-700">"{rule.value}"</span>
      </>
    )
  }
  return <span className="text-sm text-gray-700">{expression}</span>
}

// Helper to format If rule display (simplified single line)
function formatIfDisplay(rule: IfNode | undefined, expression: string): React.ReactNode {
  if (rule?.reference) {
    const conditionText = rule.condition === '!=' ? 'not equals to' 
      : rule.condition === '>' ? 'higher than'
      : rule.condition === '>=' ? 'higher or equal to'
      : rule.condition === '<' ? 'lower than'
      : rule.condition === '<=' ? 'lower or equal to'
      : rule.condition === '==' ? 'equal to'
      : rule.condition
    
    let text: React.ReactNode[] = [
      <InfoBadge key="ref" variant="neutral">{rule.reference}</InfoBadge>,
      <span key="cond"> {conditionText} "{rule.value}"</span>
    ]
    
    if (rule.returnValue) {
      text.push(<span key="ret"> return "{rule.returnValue}"</span>)
    }
    
    if (rule.else?.returnValue) {
      text.push(<span key="else"> else return "{rule.else.returnValue}"</span>)
    }
    
    return <>{text}</>
  }
  return <span>{expression}</span>
}

function formatCodeReference(reference: string): string {
  if (!reference) return ''
  return /^code\s+/i.test(reference) ? reference : `Code ${reference}`
}

function formatConditionOperator(condition: string): string {
  if (condition === '==') return '='
  return condition
}

function isBetweenConditionEntry(condition: ConditionEntry): boolean {
  return condition.condition === 'between'
}

function getPositionableConditionText(condition: ConditionEntry): string {
  return Object.entries(condition.values || {})
    .map(([position, value]) => ({ position: Number(position), value: String(value || '') }))
    .filter(item => Number.isInteger(item.position) && item.position >= 0 && item.value)
    .sort((left, right) => left.position - right.position)
    .map(item => `pos ${item.position + 1} = ${item.value}`)
    .join(', ')
}

function isConditionEntryComplete(condition: ConditionEntry): boolean {
  if (!condition.reference || !condition.condition) return false

  if (condition.values != null) {
    return condition.condition === '==' && Boolean(getPositionableConditionText(condition))
  }

  if (isBetweenConditionEntry(condition)) {
    return Boolean(condition.from && condition.to)
  }

  return Boolean(condition.value)
}

function formatConditionEntry(condition: ConditionEntry): string {
  const positionableText = getPositionableConditionText(condition)
  if (positionableText) {
    return `${formatCodeReference(condition.reference)}: ${positionableText}`
  }

  if (isBetweenConditionEntry(condition)) {
    return `${formatCodeReference(condition.reference)} ${formatConditionOperator(condition.condition)} ${condition.from} and ${condition.to}`
  }

  return `${formatCodeReference(condition.reference)} ${formatConditionOperator(condition.condition)} ${condition.value}`
}

function hasFutureDescriptionTag(description: string | null | undefined): boolean {
  if (!description) return false
  return /(^|;\s*)future\s*$/i.test(description.trim()) || /^future$/i.test(description.trim())
}

function stripFutureDescriptionTag(description: string | null | undefined): string {
  if (!description) return ''
  const trimmed = description.trim()
  if (/^future$/i.test(trimmed)) return ''
  return trimmed.replace(/;\s*future\s*$/i, '').replace(/\s*future\s*$/i, '').trim()
}

function renderRuleBadge(content: string, key: string): React.ReactNode {
  return (
    <InfoBadge key={key} variant="neutral">
      {content}
    </InfoBadge>
  )
}

function renderRuleText(content: string, key: string): React.ReactNode {
  return (
    <span key={key} className="text-sm text-gray-700">
      {content}
    </span>
  )
}

function renderFreeTextSuffix(freeText: string | null | undefined, keyPrefix: string): React.ReactNode {
  const normalizedFreeText = String(freeText || '').trim()
  if (!normalizedFreeText) return null

  if (normalizedFreeText.includes('\n')) {
    return (
      <React.Fragment key={`${keyPrefix}-free-text`}>
        {renderRuleText('; ', `${keyPrefix}-free-text-separator`)}
        <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans text-left">
          {normalizedFreeText}
        </pre>
      </React.Fragment>
    )
  }

  return renderRuleText(`; ${normalizedFreeText}`, `${keyPrefix}-free-text`)
}

function renderGroupedConditionValues(values: string[], keyPrefix: string, joiner = ' or '): React.ReactNode {
  return (
    <>
      {values.map((value, index) => (
        <React.Fragment key={`${keyPrefix}-value-${index}`}>
          {index > 0 ? renderRuleText(joiner, `${keyPrefix}-or-${index}`) : null}
          {renderRuleBadge(value, `${keyPrefix}-badge-${index}`)}
        </React.Fragment>
      ))}
    </>
  )
}

function normalizeConditionHighlightValue(value: string | null | undefined): string {
  return String(value || '').trim()
}

function normalizeConditionHighlightPositionValues(values: Record<string, string> | null | undefined): string {
  return Object.entries(values || {})
    .map(([position, value]) => [Number(position), normalizeConditionHighlightValue(value).toUpperCase()] as const)
    .filter(([position, value]) => Number.isInteger(position) && position >= 0 && value.length > 0)
    .sort((left, right) => left[0] - right[0])
    .map(([position, value]) => `${position}:${value}`)
    .join(',')
}

function isConditionHighlighted(
  condition: ConditionEntry,
  highlights: RuleValidationConditionHighlight[] = []
): boolean {
  const reference = normalizeCodeItemReference(condition.reference)
  const operator = normalizeConditionHighlightValue(condition.condition)
  const value = normalizeConditionHighlightValue(condition.value)
  const from = normalizeConditionHighlightValue(condition.from)
  const to = normalizeConditionHighlightValue(condition.to)
  const positionValues = normalizeConditionHighlightPositionValues(condition.values)

  return highlights.some(highlight => (
    normalizeCodeItemReference(highlight.reference) === reference &&
    normalizeConditionHighlightValue(highlight.condition) === operator &&
    normalizeConditionHighlightValue(highlight.value) === value &&
    normalizeConditionHighlightValue(highlight.from) === from &&
    normalizeConditionHighlightValue(highlight.to) === to &&
    normalizeConditionHighlightPositionValues(highlight.values) === positionValues
  ))
}

function renderHighlightedCondition(
  children: React.ReactNode,
  key: string,
  highlighted: boolean,
  variant: 'error' | 'warning'
): React.ReactNode {
  if (!highlighted) return <React.Fragment key={key}>{children}</React.Fragment>

  const className = variant === 'warning'
    ? 'inline-flex items-center gap-1 rounded border border-amber-500 bg-amber-100 px-1.5 py-1 ring-1 ring-amber-300'
    : 'inline-flex items-center gap-1 rounded border border-red-500 bg-red-100 px-1.5 py-1 ring-1 ring-red-300'

  return (
    <span key={key} className={className} data-rule-condition-highlight={variant}>
      {children}
    </span>
  )
}

function renderConditionEntryNode(
  condition: ConditionEntry,
  keyPrefix: string,
  highlights: RuleValidationConditionHighlight[] = [],
  highlightVariant: 'error' | 'warning' = 'error'
): React.ReactNode {
  const highlighted = isConditionHighlighted(condition, highlights)

  if (isBetweenConditionEntry(condition)) {
    return renderHighlightedCondition(
      <>
        {renderRuleBadge(formatCodeReference(condition.reference), `${keyPrefix}-reference`)}
        {renderRuleText(` ${formatConditionOperator(condition.condition)} `, `${keyPrefix}-operator`)}
        {renderRuleBadge(String(condition.from || ''), `${keyPrefix}-from`)}
        {renderRuleText(' and ', `${keyPrefix}-and`)}
        {renderRuleBadge(String(condition.to || ''), `${keyPrefix}-to`)}
      </>,
      keyPrefix,
      highlighted,
      highlightVariant
    )
  }

  const positionableText = getPositionableConditionText(condition)
  if (positionableText) {
    return renderHighlightedCondition(
      <>
        {renderRuleBadge(formatCodeReference(condition.reference), `${keyPrefix}-reference`)}
        {renderRuleText(': ', `${keyPrefix}-separator`)}
        {renderRuleBadge(positionableText, `${keyPrefix}-positions`)}
      </>,
      keyPrefix,
      highlighted,
      highlightVariant
    )
  }

  return renderHighlightedCondition(
    <>
      {renderRuleBadge(formatCodeReference(condition.reference), `${keyPrefix}-reference`)}
      {renderRuleText(` ${formatConditionOperator(condition.condition)} `, `${keyPrefix}-operator`)}
      {renderRuleBadge(String(condition.value || ''), `${keyPrefix}-value`)}
    </>,
    keyPrefix,
    highlighted,
    highlightVariant
  )
}

function renderOrConditionEntries(
  conditions: ConditionEntry[],
  keyPrefix: string,
  highlights: RuleValidationConditionHighlight[] = [],
  highlightVariant: 'error' | 'warning' = 'error'
): React.ReactNode {
  const groups = new Map<string, ConditionEntry[]>()

  conditions.forEach((condition, index) => {
    const key = isBetweenConditionEntry(condition)
      ? `between-${index}`
      : condition.values != null
      ? `positionable-${index}`
      : `${condition.reference}|||${condition.condition}`
    const existing = groups.get(key)
    if (existing) {
      existing.push(condition)
    } else {
      groups.set(key, [condition])
    }
  })

  const groupedConditions = Array.from(groups.values())

  return (
    <>
      {groupedConditions.map((group, index) => {
        const groupKey = `${keyPrefix}-group-${index}`

        return (
          <React.Fragment key={groupKey}>
            {index > 0 ? renderRuleText(' OR ', `${groupKey}-joiner`) : null}
            {group.length === 1 ? (
              renderConditionEntryNode(group[0], `${groupKey}-single`, highlights, highlightVariant)
            ) : (
              renderHighlightedCondition(
                <>
                  {renderRuleBadge(formatCodeReference(group[0].reference), `${groupKey}-reference`)}
                  {renderRuleText(` ${formatConditionOperator(group[0].condition)} `, `${groupKey}-operator`)}
                  {renderGroupedConditionValues(group.map(item => String(item.value || '')), groupKey)}
                </>,
                groupKey,
                group.some(condition => isConditionHighlighted(condition, highlights)),
                highlightVariant
              )
            )}
          </React.Fragment>
        )
      })}
    </>
  )
}

/** Values excluded together are stored as an AND of `!=` entries and read back as one clause. */
function groupExclusionConditionEntries(conditions: ConditionEntry[]): ConditionEntry[][] {
  const groups: ConditionEntry[][] = []
  const indexByKey = new Map<string, number>()

  conditions.forEach((condition, index) => {
    const mergeable = isExclusionOperator(condition.condition)
      && !isBetweenConditionEntry(condition)
      && condition.values == null
    const key = mergeable
      ? `${normalizeCodeItemReference(condition.reference)}|||${condition.condition}`
      : `entry-${index}`
    const existingIndex = indexByKey.get(key)

    if (existingIndex === undefined) {
      indexByKey.set(key, groups.length)
      groups.push([condition])
      return
    }

    groups[existingIndex].push(condition)
  })

  return groups
}

function renderAndConditionEntries(
  conditions: ConditionEntry[],
  keyPrefix: string,
  highlights: RuleValidationConditionHighlight[] = [],
  highlightVariant: 'error' | 'warning' = 'error'
): React.ReactNode[] {
  return groupExclusionConditionEntries(conditions).map((group, index) => {
    const groupKey = `${keyPrefix}-and-${index}`
    if (group.length === 1) {
      return renderConditionEntryNode(group[0], `${groupKey}-single`, highlights, highlightVariant)
    }

    return renderHighlightedCondition(
      <>
        {renderRuleBadge(formatCodeReference(group[0].reference), `${groupKey}-reference`)}
        {renderRuleText(` ${formatConditionOperator(group[0].condition)} `, `${groupKey}-operator`)}
        {renderGroupedConditionValues(group.map(item => String(item.value || '')), groupKey, ' and ')}
      </>,
      groupKey,
      group.some(condition => isConditionHighlighted(condition, highlights)),
      highlightVariant
    )
  })
}

function formatAndConditionEntries(conditions: ConditionEntry[]): string[] {
  return groupExclusionConditionEntries(conditions).map(group => {
    if (group.length === 1) return formatConditionEntry(group[0])

    const [first] = group
    return `${formatCodeReference(first.reference)} ${formatConditionOperator(first.condition)} ${group.map(item => String(item.value || '')).join(' and ')}`
  })
}

function formatOrConditionEntries(conditions: ConditionEntry[]): string {
  const groups = new Map<string, ConditionEntry[]>()

  conditions.forEach((condition, index) => {
    const key = isBetweenConditionEntry(condition)
      ? `between-${index}`
      : condition.values != null
      ? `positionable-${index}`
      : `${condition.reference}|||${condition.condition}`
    const existing = groups.get(key)
    if (existing) {
      existing.push(condition)
    } else {
      groups.set(key, [condition])
    }
  })

  return Array.from(groups.values()).map(group => {
    if (group.length === 1) {
      return formatConditionEntry(group[0])
    }

    const first = group[0]
    const values = group.map(item => String(item.value || '')).join(', ')
    return `${formatCodeReference(first.reference)} ${formatConditionOperator(first.condition)} ${values}`
  }).join(' OR ')
}

function formatConditionGroup(group: ConditionGroup | null | undefined): string {
  if (!group) return ''

  const validConditions = (group.conditions || []).filter(condition =>
    isConditionEntryComplete(condition)
  )
  const nestedGroups = (group.groups || [])
    .map(subgroup => formatConditionGroup(subgroup))
    .filter(Boolean)
    .map(text => `(${text})`)

  const parts: string[] = []
  if (group.logicalOperator === 'OR') {
    if (validConditions.length > 0) {
      parts.push(formatOrConditionEntries(validConditions))
    }
    parts.push(...nestedGroups)
    return parts.join(' OR ')
  }

  parts.push(...formatAndConditionEntries(validConditions))
  parts.push(...nestedGroups)
  return parts.join('; ')
}

function isSimpleClauseGroup(group: ConditionGroup | null | undefined): boolean {
  if (!group || (group.logicalOperator !== 'OR' && group.logicalOperator !== 'AND')) return false
  if ((group.groups || []).length > 0) return false

  const conditions = group.conditions || []
  if (conditions.length === 0) return false
  if (conditions.some(condition => isBetweenConditionEntry(condition))) return false

  const first = conditions[0]
  // An AND group is one clause only when it excludes several values of the same Code Item.
  if (group.logicalOperator === 'AND' && !isExclusionOperator(first.condition)) return false

  return conditions.every(condition =>
    (condition.reference || '') === (first.reference || '') &&
    (condition.condition || '') === (first.condition || '')
  )
}

function renderConditionGroup(
  group: ConditionGroup | null | undefined,
  keyPrefix = 'group',
  highlights: RuleValidationConditionHighlight[] = [],
  highlightVariant: 'error' | 'warning' = 'error'
): React.ReactNode {
  if (!group) return null

  const validConditions = (group.conditions || []).filter(condition =>
    isConditionEntryComplete(condition)
  )

  const nestedGroups = (group.groups || [])
    .map((subgroup, index) => {
      const rendered = renderConditionGroup(subgroup, `${keyPrefix}-nested-${index}`, highlights, highlightVariant)
      if (!rendered) return null
      const shouldWrap = !isSimpleClauseGroup(subgroup)

      return (
        <React.Fragment key={`${keyPrefix}-nested-wrap-${index}`}>
          {shouldWrap ? renderRuleText('(', `${keyPrefix}-nested-open-${index}`) : null}
          {rendered}
          {shouldWrap ? renderRuleText(')', `${keyPrefix}-nested-close-${index}`) : null}
        </React.Fragment>
      )
    })
    .filter(Boolean)

  const parts: React.ReactNode[] = []

  if (group.logicalOperator === 'OR') {
    if (validConditions.length > 0) {
      parts.push(renderOrConditionEntries(validConditions, `${keyPrefix}-conditions`, highlights, highlightVariant))
    }
    parts.push(...nestedGroups)

    return (
      <>
        {parts.map((part, index) => (
          <React.Fragment key={`${keyPrefix}-part-${index}`}>
            {index > 0 ? renderRuleText(' OR ', `${keyPrefix}-join-${index}`) : null}
            {part}
          </React.Fragment>
        ))}
      </>
    )
  }

  parts.push(...renderAndConditionEntries(validConditions, keyPrefix, highlights, highlightVariant))
  parts.push(...nestedGroups)

  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={`${keyPrefix}-part-${index}`}>
          {index > 0 ? renderRuleText(' AND ', `${keyPrefix}-join-${index}`) : null}
          {part}
        </React.Fragment>
      ))}
    </>
  )
}

function renderConsequenceGroups(
  groups: ConditionGroup[] | null | undefined,
  keyPrefix = 'consequence',
  highlights: RuleValidationConditionHighlight[] = [],
  highlightVariant: 'error' | 'warning' = 'error'
): React.ReactNode {
  const validGroups = (groups || []).filter(Boolean)
  if (validGroups.length === 0) return null

  return (
    <div className="flex w-full flex-col gap-1.5">
      {validGroups.map((group, index) => (
        <div key={`${keyPrefix}-${index}`} className="flex w-full items-start gap-2 text-left">
          <span className="pt-0.5 text-sm text-gray-700">{index + 1}.</span>
          <div className="flex flex-1 items-center gap-1 flex-wrap">
            {renderConditionGroup(group, `${keyPrefix}-${index}`, highlights, highlightVariant)}
          </div>
        </div>
      ))}
    </div>
  )
}

export const AvailabilityBadge: React.FC<AvailabilityBadgeProps> = ({ 
  value, 
  onConfigClick,
  showConfigIcon = true,
  disabled = false,
  conditionHighlights = [],
  conditionHighlightVariant = 'error'
}) => {
  const handleConfigClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!disabled && onConfigClick) {
      onConfigClick()
    }
  }

  // Render the config icon button
  const renderConfigIcon = () => {
    if (!showConfigIcon) return null
    return (
      <button
        type="button"
        onClick={handleConfigClick}
        disabled={disabled}
        className={`p-1 rounded transition-colors flex-shrink-0 ${
          disabled 
            ? 'text-gray-300 cursor-not-allowed' 
            : 'text-gray-400 hover:text-[#1D74D8] hover:bg-blue-50'
        }`}
        title="Configure availability rule"
      >
        <Settings2 className="w-4 h-4" />
      </button>
    )
  }

  if (!value) {
    return (
      <div className="flex items-center gap-2 w-full">
        <div className="flex-1" />
        {renderConfigIcon()}
      </div>
    )
  }

  // "While" rule type
  if (value.type === 'while') {
    const whileRule = value as LoopNode
    const expression = value.description || `${whileRule.reference} ${whileRule.condition} "${whileRule.value}"`
    return (
      <div className="flex items-center gap-2 w-full">
        <div className="flex-1 flex items-center gap-1 flex-wrap text-sm text-gray-700">
          <span>While</span>
          {formatWhileDisplay(whileRule, expression)}
          {renderFreeTextSuffix(value.freeText, 'while')}
        </div>
        {renderConfigIcon()}
      </div>
    )
  }

  // "If" rule type
  if (value.type === 'if') {
    const ifRule = value as IfNode
    const hasFutureTag = Boolean(ifRule.future) || hasFutureDescriptionTag(value.description)
    const displayDescription = hasFutureTag ? stripFutureDescriptionTag(value.description) : (value.description || '')

    if (ifRule.conditionGroup && (ifRule.consequenceGroups?.length || 0) > 0) {
      return (
        <div className="flex items-start gap-2 w-full">
          <div className="flex flex-1 flex-col items-start gap-1.5 text-left">
            <div className="flex w-full items-start gap-1 flex-wrap text-left">
              <span className="text-sm font-medium text-gray-700 shrink-0">IF</span>
              <div className="flex min-w-0 flex-1 items-center gap-1 flex-wrap">
                {renderConditionGroup(ifRule.conditionGroup, 'if-trigger', conditionHighlights, conditionHighlightVariant)}
              </div>
            </div>
            <div className="w-full text-left text-sm font-medium text-gray-700">THEN</div>
            {renderConsequenceGroups(ifRule.consequenceGroups, 'if-consequence', conditionHighlights, conditionHighlightVariant)}
            <div className="flex w-full items-start gap-1 flex-wrap text-left">
              {hasFutureTag ? renderRuleBadge('Future', 'future-badge-structured') : null}
              {renderFreeTextSuffix(value.freeText, 'if-consequence-free-text')}
            </div>
          </div>
          {renderConfigIcon()}
        </div>
      )
    }

    if (ifRule.conditionGroup) {
      const conditionGroupText = formatConditionGroup(ifRule.conditionGroup)

      return (
        <div className="flex items-center gap-2 w-full">
          <div className="flex-1 flex items-center gap-1 flex-wrap">
            {renderConditionGroup(ifRule.conditionGroup, 'group', conditionHighlights, conditionHighlightVariant)}
            {hasFutureTag ? (
              <>
                {renderRuleText(conditionGroupText ? '; ' : '', 'future-separator')}
                {renderRuleBadge('Future', 'future-badge')}
              </>
            ) : null}
            {renderFreeTextSuffix(value.freeText, 'condition-group')}
          </div>
          {renderConfigIcon()}
        </div>
      )
    }

    // If we have a description or a future marker, render them consistently.
    if (displayDescription || hasFutureTag) {
      const hasMultipleLines = displayDescription.includes('\n')
      return (
        <div className="flex items-start gap-2 w-full">
          <div className="flex-1">
            <div className="flex items-start gap-1 flex-wrap">
              {displayDescription ? (hasMultipleLines ? (
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans text-left">
                  {displayDescription.split('\n').map((line, i) => {
                    const parts = line.split(/(\b[A-Z]\d+\b)/g)
                    return (
                      <div key={i}>
                        {parts.map((part, j) => 
                          /^[A-Z]\d+$/.test(part) 
                            ? <InfoBadge key={j} variant="neutral">{part}</InfoBadge>
                            : <span key={j}>{part}</span>
                        )}
                      </div>
                    )
                  })}
                </pre>
              ) : (
                <span className="text-sm text-gray-700 whitespace-normal text-left">
                  {displayDescription.split(/(\b[A-Z]\d+\b)/g).map((part, j) => 
                    /^[A-Z]\d+$/.test(part) 
                      ? <InfoBadge key={j} variant="neutral">{part}</InfoBadge>
                      : <span key={j}>{part}</span>
                  )}
                </span>
              )) : null}
              {hasFutureTag ? renderRuleBadge('Future', 'future-badge-description') : null}
            </div>
          </div>
          {renderConfigIcon()}
        </div>
      )
    }

    // Fallback to simplified format for simple rules without description
    const expression = `If ${ifRule.reference} ${ifRule.condition} "${ifRule.value}" return "${ifRule.returnValue}"`
    return (
      <div className="flex items-center gap-2 w-full">
        <div className="flex-1 flex items-center gap-1 flex-wrap">
          <span>If</span>
          {formatIfDisplay(ifRule, expression)}
        </div>
        {renderConfigIcon()}
      </div>
    )
  }

  if (value.type === 'text') {
    const textRule = value as TextRuleNode
    const text = textRule.description || textRule.freeText || ''

    return (
      <div className="flex items-start gap-2 w-full">
        <div className="flex-1 flex items-start gap-1 flex-wrap text-sm text-gray-700">
          {text ? (
            text.includes('\n') ? (
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans text-left">{text}</pre>
            ) : (
              <span className="text-sm text-gray-700 whitespace-normal text-left">{text}</span>
            )
          ) : null}
        </div>
        {renderConfigIcon()}
      </div>
    )
  }

  // Fallback
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1">
        <span className="italic text-gray-400">Unknown</span>
      </div>
      {renderConfigIcon()}
    </div>
  )
}
